"use client";

import type { ReactElement } from "react";
import type { HarnessId, ModelProvider } from "@bash/shared";
import type { Brand } from "./model-catalog";
import { VestraLogoMark } from "./VestraLogoMark";
import { cn } from "@/lib/utils";
import { ClaudeLogo } from "@/components/icons/ClaudeLogo";
import { OpenAILogo } from "@/components/icons/OpenAILogo";
import { ZhipuLogo } from "@/components/icons/ZhipuLogo";
import { FireworksLogo } from "@/components/icons/FireworksLogo";
import { DeepSeekLogo } from "@/components/icons/DeepSeekLogo";
import { XAILogo } from "@/components/icons/XAILogo";
import { GeminiLogo } from "@/components/icons/GeminiLogo";

export {
  ClaudeLogo,
  OpenAILogo,
  ZhipuLogo,
  FireworksLogo,
  DeepSeekLogo,
  XAILogo,
  GeminiLogo,
};
export { CursorLogo } from "@/components/icons/CursorLogo";
export { OpenClawLogo } from "@/components/icons/OpenClawLogo";
export { HermesLogo } from "@/components/icons/HermesLogo";
export { CodexLogo } from "@/components/icons/CodexLogo";

/**
 * Shared brand / provider marks + rounded icon tiles for the composer's
 * model picker. Every mark is the OFFICIAL brand asset (no recreated
 * approximations) so provider rows read instantly as the real company. Sizing/
 * tile shape matches the house style used by ModeSelector (rounded tile,
 * theme-var backgrounds). The mark components themselves live in
 * @/components/icons/ — this file only resolves which mark/tile a given
 * provider, brand, or harness gets.
 */

/* ── Mark resolvers ──────────────────────────────────────────────────────── */

type Mark = (props: {
  width?: number | string;
  height?: number | string;
  className?: string;
}) => ReactElement;

const PROVIDER_MARK: Record<ModelProvider, Mark> = {
  anthropic: ClaudeLogo,
  openai: OpenAILogo,
  fireworks: FireworksLogo,
  xai: XAILogo,
  // DeepSeek first-party lane (V4 Pro official API) — same whale mark the
  // brand rail has always shown; the infra cutover is invisible in the picker.
  deepseek: DeepSeekLogo,
  google: GeminiLogo,
};

/** The brand mark for a model PROVIDER (used by group headers + model rows). */
export function providerMark(provider: ModelProvider): Mark {
  return PROVIDER_MARK[provider] ?? ClaudeLogo;
}

/** Per-BRAND mark — the display identity in the picker (decoupled from the infra
 *  provider). Inherits the provider marks; adds the per-family open-model brands
 *  (DeepSeek + GLM/Z.ai both ride the `fireworks` provider but keep their own marks). */
const BRAND_MARK: Record<Brand, Mark> = {
  ...PROVIDER_MARK,
  deepseek: DeepSeekLogo,
  zhipu: ZhipuLogo,
};

/** The BARE brand mark for a display brand — the Deck's card crests draw the
 *  mark directly on a light washed face (no tile), so marks that are
 *  light-on-dark by brand (Z.ai's white slash-Z) flip to ink here. */
export function BareBrandMark({
  brand,
  size,
  ink = false,
}: {
  brand: Brand;
  size: number;
  /** Draw in `currentColor` — for a watermark that takes the surface's ink
   *  rather than the brand's colour (the panes picker's house backdrop). */
  ink?: boolean;
}): ReactElement {
  if (brand === "zhipu")
    return (
      <ZhipuLogo
        width={size}
        height={size}
        fill={ink ? "currentColor" : "#0b0b0c"}
      />
    );
  if (ink && brand === "anthropic")
    return <ClaudeLogo width={size} height={size} fill="currentColor" />;
  const Mark = BRAND_MARK[brand] ?? ClaudeLogo;
  return <Mark width={size} height={size} />;
}

const HARNESS_MARK: Record<HarnessId, Mark> = {
  claude: ClaudeLogo,
  native: VestraLogoMark, // Vestra brand mark — the multi-model (all models) harness.
  openai: OpenAILogo,
  openai_responses: OpenAILogo,
  claude_code: ClaudeLogo, // the CLI on the user's own Anthropic plan — its own brand
  // The Codex engine (engines T3) gets no vendor logo: an engine is named in plain text only.
  // The product's own mark stands in, on the neutral tile below.
  codex: VestraLogoMark,
};

/** The brand mark for a HARNESS (used by left-pane harness rows + trigger). */
export function harnessMark(value: HarnessId): Mark {
  return HARNESS_MARK[value] ?? ClaudeLogo;
}

/* ── Tile wrappers (house style: rounded, theme-var background) ───────────── */

/** Per-provider tile background tint (subtle, theme-aware) — matches ModeSelector ICON_BG. */
const PROVIDER_TILE_BG: Record<ModelProvider, string> = {
  anthropic:
    "bg-gradient-to-br from-[#fbeee6] to-[#fdf6f0] dark:from-[#3a2a20] dark:to-[#2a1e16]",
  openai:
    "bg-gradient-to-br from-[#e8e8ec] to-[#f5f5f7] dark:from-[#28282f] dark:to-[#1a1a22]",
  // Fireworks brand is violet — a saturated purple tile so the white spark glyph
  // reads on both light and near-black popover surfaces.
  fireworks:
    "bg-gradient-to-br from-[#6d28d9] to-[#7c3aed] ring-1 ring-inset ring-black/10 dark:ring-white/12",
  xai: "bg-gradient-to-br from-[#e8e8ec] to-[#f5f5f7] dark:from-[#28282f] dark:to-[#1a1a22]",
  // DeepSeek first-party — the same soft-blue whale field the brand tile uses.
  deepseek:
    "bg-gradient-to-br from-[#eef1ff] to-[#f5f7ff] dark:from-[#1a2050] dark:to-[#131838]",
  google:
    "bg-gradient-to-br from-[#e8eefb] to-[#f3f6fd] dark:from-[#1a2540] dark:to-[#131b2e]",
};

/** Per-BRAND tile tint. Inherits provider tiles; the per-family open-model brands keep
 *  their own tiles (DeepSeek's blue whale on a soft blue field; GLM/Z.ai's white "Z." on
 *  a solid black tile + faint ring so it reads on both light and near-black surfaces). */
const BRAND_TILE_BG: Record<Brand, string> = {
  ...PROVIDER_TILE_BG,
  deepseek:
    "bg-gradient-to-br from-[#eef1ff] to-[#f5f7ff] dark:from-[#1a2050] dark:to-[#131838]",
  zhipu:
    "bg-extension-body-notice ring-1 ring-inset ring-black/10 dark:ring-white/12",
};

const HARNESS_TILE_BG: Record<HarnessId, string> = {
  claude: PROVIDER_TILE_BG.anthropic,
  native: "bg-gradient-to-br from-[#2a2a2a] to-[#0d0d0d]", // dark Vestra tile (brand)
  openai: PROVIDER_TILE_BG.openai,
  openai_responses: PROVIDER_TILE_BG.openai,
  claude_code: PROVIDER_TILE_BG.anthropic,
  codex: "bg-gradient-to-br from-[#2a2a2a] to-[#0d0d0d]", // the Vestra tile: no vendor colours either
};

/**
 * Rounded icon tile rendering a PROVIDER's brand mark. `tileSize` is the tile
 * box; the mark renders at ~58% of it (matching ModeSelector's icon-in-tile ratio).
 */
export function ProviderIconTile({
  provider,
  tileSize = 28,
}: {
  /** Display brand (a superset of ModelProvider — e.g. 'deepseek'). */
  provider: Brand;
  tileSize?: number;
}): ReactElement {
  const Mark = BRAND_MARK[provider] ?? ClaudeLogo;
  const radius = Math.round(tileSize * 0.32);
  // The SpaceXAI swoosh is a flat-aspect mark — render it wider in the tile so
  // its visual weight matches the square marks.
  const frac = provider === "xai" ? 0.82 : 0.58;
  return (
    <span
      className={cn(
        "inline-flex items-center justify-center flex-shrink-0",
        BRAND_TILE_BG[provider]
      )}
      style={{ width: tileSize, height: tileSize, borderRadius: radius }}
    >
      <Mark
        width={Math.round(tileSize * frac)}
        height={Math.round(tileSize * frac)}
      />
    </span>
  );
}

/** Rounded icon tile rendering a HARNESS's brand mark. */
export function HarnessIconTile({
  value,
  tileSize = 36,
}: {
  value: HarnessId;
  tileSize?: number;
}): ReactElement {
  const Mark = harnessMark(value);
  const radius = Math.round(tileSize * 0.3);
  return (
    <span
      className={cn(
        "inline-flex items-center justify-center flex-shrink-0",
        HARNESS_TILE_BG[value]
      )}
      style={{ width: tileSize, height: tileSize, borderRadius: radius }}
    >
      <Mark
        width={Math.round(tileSize * 0.55)}
        height={Math.round(tileSize * 0.55)}
      />
    </span>
  );
}
